import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../App";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Clock, AlertTriangle } from "lucide-react";

/**
 * Idle-timeout guard.
 *
 *  - After tenant.idle_timeout_minutes (default 5 min) of no mouse/keyboard/
 *    touch activity, a warning Dialog opens.
 *  - The Dialog counts down 60s and shows a live timer.
 *  - "I'm still here" resets both timers — back to a fresh idle window.
 *  - If the user doesn't respond in time, we call `logout()` from AuthContext.
 *
 * Mounted once per authenticated session (inside Layout.jsx so super-admin
 * /admin pages get it too). No-op when there's no logged-in user.
 *
 * Tracked events: mousemove, mousedown, keydown, scroll, touchstart, wheel.
 */
const IdleTimeoutGuard = ({ warningMs = 60_000 }) => {
  const { user, tenant, logout } = useAuth();

  // Tenant-configured minutes (5/10/15/30/60). Fallback to 5 if unset/invalid.
  const tenantMinutes = Number(tenant?.idle_timeout_minutes);
  const idleMs = ([5, 10, 15, 30, 60].includes(tenantMinutes) ? tenantMinutes : 5) * 60_000;

  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warningMs / 1000));

  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const tickRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }, []);

  const startWarning = useCallback(() => {
    setSecondsLeft(Math.ceil(warningMs / 1000));
    setWarning(true);
    // Ticking countdown for the live timer text.
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    // Hard logout when warning expires.
    warnTimerRef.current = setTimeout(async () => {
      clearTimers();
      setWarning(false);
      try {
        await logout();
      } catch { /* ignore — logout already redirected */ }
    }, warningMs);
  }, [warningMs, logout, clearTimers]);

  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      startWarning();
    }, idleMs);
  }, [idleMs, startWarning]);

  const resetIdle = useCallback(() => {
    // If the warning is open, don't silently dismiss it — the user must click
    // Continue. Activity during the warning window is intentionally ignored
    // so an accidental mouse move doesn't extend the session.
    if (warning) return;
    armIdleTimer();
  }, [warning, armIdleTimer]);

  const handleContinue = useCallback(() => {
    clearTimers();
    setWarning(false);
    armIdleTimer();
  }, [clearTimers, armIdleTimer]);

  useEffect(() => {
    if (!user) {
      clearTimers();
      return;
    }

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    const onActivity = () => resetIdle();
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    armIdleTimer();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, idleMs]);

  if (!user) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <Dialog open={warning} onOpenChange={() => { /* lock until user picks */ }}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="idle-timeout-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            Are you still there?
          </DialogTitle>
          <DialogDescription>
            For security, we&apos;ll sign you out due to inactivity.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-3xl font-mono font-bold text-[#E11D48]" data-testid="idle-timeout-countdown">
            <Clock className="w-7 h-7" />
            {mm}:{ss}
          </div>
          <p className="text-sm text-slate-500">
            until automatic sign-out
          </p>
        </div>
        <DialogFooter className="flex-row gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              clearTimers();
              setWarning(false);
              try { await logout(); } catch { /* ignore */ }
            }}
            data-testid="idle-timeout-logout-btn"
            className="flex-1"
          >
            Sign out now
          </Button>
          <Button
            onClick={handleContinue}
            className="bg-[#0D9488] hover:bg-[#14B8A6] flex-1"
            data-testid="idle-timeout-continue-btn"
          >
            I&apos;m still here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IdleTimeoutGuard;
